// Table of contents — list every slide with a clickable title that jumps the
// deck to that slide. Reads slide titles directly from the deck DOM in the
// iframe (same-origin, so we have full access).

function titleOf(section) {
  // Prefer explicit data-title; fall back to first heading; finally first text.
  const dt = section.getAttribute('data-title');
  if (dt) return { text: dt, kind: 'titled' };
  const h = section.querySelector('h1, h2, h3, h4');
  if (h && h.textContent.trim()) return { text: h.textContent.trim(), kind: 'titled' };
  // Take the first non-empty text node we can find as a hint.
  const txt = (section.textContent || '').trim().split('\n').map(s => s.trim()).filter(Boolean)[0] || '';
  if (txt) return { text: txt.slice(0, 60) + (txt.length > 60 ? '…' : ''), kind: 'untitled' };
  return { text: '(untitled)', kind: 'untitled' };
}

/** Build the entry list. Each entry = { h, v, title, kind, isVert }. */
function buildEntries(reveal) {
  const root = reveal.getRevealElement?.() ?? reveal.getViewportElement?.()?.querySelector('.reveal') ?? document;
  const slidesRoot = root.querySelector('.slides');
  if (!slidesRoot) return [];
  const entries = [];
  const horiz = [...slidesRoot.children].filter(n => n.tagName === 'SECTION');
  horiz.forEach((h, hi) => {
    const verticals = [...h.children].filter(n => n.tagName === 'SECTION');
    if (verticals.length === 0) {
      entries.push({ h: hi, v: 0, ...titleOf(h), isVert: false, hasVert: false });
    } else {
      entries.push({ h: hi, v: 0, ...titleOf(h), isVert: false, hasVert: true });
      verticals.forEach((vs, vi) => {
        entries.push({ h: hi, v: vi, ...titleOf(vs), isVert: true, hasVert: true });
      });
    }
  });
  return entries;
}

export default {
  id: 'toc',
  slot: 'toc',
  mount({ slot, deck }) {
    slot.innerHTML = `
      <div class="panel-header"><span>Outline</span><span data-role="count"></span></div>
      <div class="toc-body" data-role="body"></div>
    `;
    const body  = slot.querySelector('[data-role="body"]');
    const count = slot.querySelector('[data-role="count"]');

    let entries = [];

    const render = () => {
      const cur = deck.getIndices();
      body.innerHTML = entries.map((e, i) => {
        const isCurrent = e.h === cur.h && e.v === (cur.v ?? 0);
        const cls = ['toc-item',
          isCurrent ? 'current' : '',
          e.kind === 'untitled' ? 'untitled' : '',
          e.isVert ? 'is-vert' : '',
          e.hasVert ? 'has-vert' : '',
        ].filter(Boolean).join(' ');
        const num = e.isVert ? '' : (e.h + 1);
        const escaped = e.text.replace(/[&<>]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;'}[c]));
        return `<div class="${cls}" data-h="${e.h}" data-v="${e.v}">
          <span class="num">${num}</span>
          <span class="title">${escaped}</span>
        </div>`;
      }).join('');
      count.textContent = `${entries.length} slide${entries.length === 1 ? '' : 's'}`;
      // Keep the current entry visible.
      const cel = body.querySelector('.toc-item.current');
      if (cel) cel.scrollIntoView({ block: 'nearest' });
    };

    body.addEventListener('click', (e) => {
      const item = e.target.closest('.toc-item');
      if (!item) return;
      deck.reveal?.slide(parseInt(item.dataset.h, 10), parseInt(item.dataset.v, 10), -1);
    });

    deck.onReady((reveal) => {
      entries = buildEntries(reveal);
      render();
      reveal.on('slidechanged', render);
    });
  },
};
